// Patch 29 · shared pieces for Apoio ⚡ / seasons: pixel bolt, pixel slot number, 20-block bar, season labels, support call.
import { api } from "@/lib/api";
import { buzz } from "@/lib/buzz";

const BOLT = ["...##", "..##.", ".##..", "####.", "..##.", ".##..", "##..."];
const DIG = {
  0: [".###.", "#...#", "#..##", "#.#.#", "##..#", "#...#", ".###."],
  1: ["..#..", ".##..", "..#..", "..#..", "..#..", "..#..", ".###."],
  2: [".###.", "#...#", "....#", "...#.", "..#..", ".#...", "#####"],
  3: ["####.", "....#", "....#", ".###.", "....#", "....#", "####."],
  4: ["...#.", "..##.", ".#.#.", "#..#.", "#####", "...#.", "...#."],
  5: ["#####", "#....", "####.", "....#", "....#", "#...#", ".###."],
  6: [".###.", "#....", "#....", "####.", "#...#", "#...#", ".###."],
  7: ["#####", "....#", "...#.", "..#..", ".#...", ".#...", ".#..."],
  8: [".###.", "#...#", "#...#", ".###.", "#...#", "#...#", ".###."],
  9: [".###.", "#...#", "#...#", ".####", "....#", "....#", ".###."],
};
export const RANK_C = ["#F5C518", "#D9D4C8", "#D08A4E", "#8a847c"];
const RANK_D = ["#8a6400", "#6b675f", "#6b3d16", "#3d3a35"];

function rects(rows) {
  const out = [];
  rows.forEach((row, y) => [...row].forEach((ch, x) => ch === "#" && out.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />)));
  return out;
}

export function Bolt({ color = "#F5C518", size = 14, className = "" }) {
  return (
    <svg viewBox="0 0 5 7" width={(size * 5) / 7} height={size} shapeRendering="crispEdges" fill={color} className={`inline-block shrink-0 ${className}`} aria-hidden>
      {rects(BOLT)}
    </svg>
  );
}

/** Pixel inventory-slot number (design A): double frame in gold / silver / bronze / grey; #1 glows. Any number of digits. */
export function SlotNum({ n, size = 40 }) {
  const i = Math.min(3, n - 1);
  const c = RANK_C[i];
  const digits = String(Math.max(0, n)).split("");
  const w = digits.length * 6 - 1;
  const out = [];
  digits.forEach((dg, k) => DIG[dg].forEach((row, y) => [...row].forEach((ch, x) => ch === "#" && out.push(<rect key={`${k}-${x}-${y}`} x={k * 6 + x} y={y} width="1" height="1" />))));
  const h = Math.round(size * 0.53);
  const px = h / 7;
  return (
    <span className={`slotnum ${n === 1 ? "is-top" : ""}`} style={{ "--c": c, "--d": RANK_D[i], width: size, height: size }} aria-label={`#${n}`}>
      <svg viewBox={`0 0 ${w} 7`} width={Math.min(size - 8, Math.round(w * px * (digits.length > 2 ? 0.75 : 1)))} height={digits.length > 2 ? h * 0.75 : h} shapeRendering="crispEdges" fill={c} aria-hidden>
        {out}
      </svg>
    </span>
  );
}

export function Segs({ fill, color, glow = false }) {
  const f = Math.max(0, Math.min(20, Math.round(fill)));
  return (
    <span className="segs" aria-hidden>
      {Array.from({ length: 20 }, (_, k) => (
        <i key={k} className={k < f ? "f" : ""} style={k < f ? { background: color, boxShadow: glow ? `0 0 6px ${color}` : undefined, transitionDelay: `${k * 25}ms` } : { transitionDelay: `${k * 25}ms` }} />
      ))}
    </span>
  );
}

// 3×5 pixel glyphs for the "+1 XP" pop
const G = { "+": [".#.", "###", ".#.", "...", "..."], 1: [".#.", "##.", ".#.", ".#.", "###"], X: ["#.#", "#.#", ".#.", "#.#", "#.#"],
  P: ["##.", "#.#", "##.", "#..", "#.."], " ": ["..", "..", "..", "..", ".."] };
export function PixelText({ text = "+1 XP", px = 3, color = "#F5C518" }) {
  let x = 0;
  const out = [];
  [...text].forEach((ch) => {
    const g = G[ch] || G[" "];
    g.forEach((row, y) => [...row].forEach((c, cx) => c === "#" && out.push(<rect key={`${x + cx}-${y}`} x={x + cx} y={y} width="1" height="1" />)));
    x += g[0].length + 1;
  });
  return (
    <svg viewBox={`-0.5 -0.5 ${x} 6`} width={x * px} height={6 * px} shapeRendering="crispEdges" fill={color} aria-hidden>{out}</svg>
  );
}

export function seasonNum(s) {
  return Math.max(1, (s.year - 2026) * 4 + s.q - 3);
}

export function seasonLabel(s, H) {
  const m = H.months;
  return `${H.season.replace("{n}", seasonNum(s))} · ${m[(s.q - 1) * 3]}–${m[(s.q - 1) * 3 + 2]} ${s.year}`;
}

export function daysLeft(s) {
  return Math.max(0, Math.ceil((new Date(s.ends_at).getTime() - Date.now()) / 86400000) - 1);
}

export function endsText(s, H) {
  const d = daysLeft(s);
  return d <= 0 ? H.ends_0 : d === 1 ? H.ends_1 : H.ends_in.replace("{n}", d);
}

/** POST /support/{id}; returns the result or throws an Error whose message is already translated. */
export async function giveSupport(entryId, H) {
  try {
    const { data } = await api.post(`/support/${entryId}`);
    buzz(25);
    return data;
  } catch (e) {
    const d = e?.response?.data?.detail || "";
    if (typeof d === "string" && d.startsWith("muted:")) throw Object.assign(new Error(""), { silent: true });   // the api interceptor already said it
    const code = typeof d === "string" && d.startsWith("support:") ? d.slice(8) : "generic";
    throw new Error(H.err[code] || H.err.generic);
  }
}

/** One toast line for streak milestone rewards in a support result (or ""). */
export function rewardText(res, H) {
  const r = (res?.rewards || []).slice(1).find(Boolean);
  if (!r) return "";
  if (r.item) return H.scepter_got;
  const what = r.shards ? `+${r.shards} Holo Shards` : r.chest ? H.ladder[5] : r.pack ? H.ladder[7] : "";
  return H.streak_got.replace("{n}", res.streak).replace("{r}", what);
}
